import { defineComponent as _defineComponent } from 'vue'
import {getExampleNumber, isValidPhoneNumber} from 'libphonenumber-js/max';
import {debounce} from 'lodash-es';
import {CountryCode} from 'libphonenumber-js/types';
import {VCol, VLayout, VRow} from 'vuetify/lib';
import {IMultiformNationalityDictionaryElement} from '@/modules/multiForm/shared/IMultiformDictionary';
import MultiformDictionary from '@/modules/multiForm/shared/MultiformDictionaries';
import EnvironmentService from '@/env/EnvironmentService';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {PhoneNumberObject} from '@/models/PhoneNumberObject';
import {computed, onMounted, ref, watch} from 'vue';
import {AutomationTestsIds} from '@/env/AutomationTests/ids/AutomationTestsIds';

type ValidationRule = ((v: string) => string | boolean)


export default /*#__PURE__*/_defineComponent({
  __name: 'FpInternationalNumber',
  props: {
    prependIcon: { default: '' },
    label: { default: () => I18NGetter().fpInternationalNumber.PHONE_INPUT_LABEL },
    inputId: null,
    selectId: null,
    value: { default: '' },
    errorMessages: { default: '' },
    disabled: { type: Boolean, default: false }
  },
  emits: ['input', 'change', 'blur', 'prefix-change',],
  setup(__props: any, { expose, emit }) {

const props = __props as { prependIcon: string, label: string, inputId?: string, selectId?: string, value: string, errorMessages: string, disabled: boolean };

const exampleNumbers = require('libphonenumber-js/examples.mobile.json');




const phoneInputRef = ref<{ $refs: { fpInput: { resetValidation: () => void } } } | null>(null);

const phoneNumberObject = ref(new PhoneNumberObject(props.value));
const phoneNumberMask = ref<Nullable<string>>(null);
const searchInput = ref('');
const isNewQuery = ref(false);

watch(() => props.value, () => {
  if (props.value) {
    phoneNumberObject.value.phoneNumber = props.value.slice(phoneNumberObject.value.callingCode!.length + 1);
  } else {
    phoneNumberObject.value.phoneNumber = null;
  }
});

const countryFlag = computed(() => {
  return getFlagEmoji(countryCode.value || EnvironmentService.Environment.getAppDomainConfig().region);
});

const countryCode = computed(() => {
  return phoneNumberObject.value?.type || null;
});

function getFlagEmoji(countryCode: CountryCode): string {
  return String.fromCodePoint(...[...countryCode.toUpperCase(),].map(x => 0x1f1a5 + x.charCodeAt(0)));
}

const isValidNumber = computed(() => {
  if (finalValue.value) {
    if (!phoneNumberMask.value) {
      // For non-PL/RO prefixes, check if length is between 8-11 digits
      const numberLength = phoneNumberObject.value.phoneNumber?.replace(/\D/g, '').length || 0;
      return numberLength >= 8 && numberLength <= 11;
    }
    return isValidPhoneNumber(finalValue.value);
  } else return false;
});

const countries = computed(() => {
  if (EnvironmentService.Environment.getAppDomainConfig().availablePhonePrefixesCountryCodes.length) {
    return MultiformDictionary.countriesDictionary
      .filter(country => EnvironmentService.Environment.getAppDomainConfig().availablePhonePrefixesCountryCodes.includes(country.type))
  }
  return MultiformDictionary.countriesDictionary;
});

const getDebounce = debounce(() => {
  isNewQuery.value = true;
}, 2000);

function onSearchInput(event: string) {
  if (event && event.length >= searchInput.value.length) {
    if (isNewQuery.value) {
      searchInput.value = event.slice(-1);
      isNewQuery.value = false;
    } else {
      searchInput.value = event;
    }
  } else {
    searchInput.value = '';
  }
  return getDebounce();
}

const searchInputNumberDisplay = computed(() => {
  return searchInput.value.replace(/[^\d]/g, '');
});

const finalValue = computed(() => {
  return phoneNumberObject.value.callingCode && phoneNumberObject.value.phoneNumber
    ? `+${phoneNumberObject.value.callingCode}${phoneNumberObject.value.phoneNumber}`
    : null;
});

onMounted(() => {
  setDefaultMask();
});

function setDefaultMask(): void {
  const countryToUse = phoneNumberObject.value.type
    ? countries.value.find(c => c.type === phoneNumberObject.value.type)
    : countries.value[0];
  if (countryToUse) {
    setMask(countryToUse);
  }
}

function resetPhoneInputValidation(): void {
  phoneInputRef.value?.$refs?.fpInput?.resetValidation?.();
}

function setMaskAndCountry(event: IMultiformNationalityDictionaryElement<CountryCode>): void {
  if (event) {
    setMask(event);
    phoneNumberObject.value.callingCode = event.callingCode;
    phoneNumberObject.value.type = event.type;
    phoneNumberObject.value.flag = event.flag;
    phoneNumberObject.value.phoneNumber = null;
    emit('input', null);
    emit('prefix-change');
    resetPhoneInputValidation();
  } else {
    console.warn(I18NGetter().fpInternationalNumber.PHONE_SET_MASK_WARN);
  }
}

function setMask(event: IMultiformNationalityDictionaryElement<CountryCode>) {
  const number = getExampleNumber(event?.type, exampleNumbers);
  if (number) {
    // Array of Countries with mask. TODO: W przyszłości to trzeba wyciągnąć do appDomainConfig
    const maskedCountries: CountryCode[] = ['PL', 'RO',];
    if (maskedCountries.includes(event.type)) {
      // Create mask format from example national phone number
      phoneNumberMask.value = number.formatNational()
        .replace(/^0/, '')
        .replace(/[()]/g, '')
        .replace('-', '')
        .replace(/[\d]/g, '#');
    } else {
      phoneNumberMask.value = null;
    }
  }
}
const customPhoneRules = computed((): ValidationRule[] => {
  return [
    (v: string) => {
      if (!phoneNumberMask.value) {
        const numberLength = v?.replace(/\D/g, '').length || 0;
        if (numberLength < 8 || numberLength > 11) {
          return I18NGetter().fpInternationalNumber.PHONE_LENGTH_RULES_MSG;
        }
      }
      return isValidNumber.value || props.errorMessages || I18NGetter().fpInternationalNumber.PHONE_CUSTOM_RULES_MSG;
    },
  ];
});

function emitValue() {
  if (isValidNumber.value) {
    emit('input', finalValue.value);
  }
}

function onBlur(event: FocusEvent) {
  if (isValidNumber.value) {
    emit('blur', event);
  }
}

function onPaste(event: ClipboardEvent) {
  try {
    event.stopPropagation();
    event.preventDefault();
    const paste = (event.clipboardData || (window as any).clipboardData).getData('text');
    phoneNumberObject.value = new PhoneNumberObject(paste, countryCode.value);
    const countryElementFound = countries.value.find(x => x.type === countryCode.value);
    setMask(countryElementFound!);
    emitValue();
  } catch (e) {
    console.error(e)
    console.warn('Occurred error when parsing pasted phone number');
  }
}

// Directive
const vOmitInputElement = {
  mounted: (el: HTMLElement) => {
    el.querySelector('input')!.setAttribute('tabindex', '-1');
  },
};

expose({
  validate: () => {
    return isValidNumber.value;
  },
  resetValidation: resetPhoneInputValidation,
});

return { __sfc: true,exampleNumbers, props, emit, phoneInputRef, phoneNumberObject, phoneNumberMask, searchInput, isNewQuery, countryFlag, countryCode, getFlagEmoji, isValidNumber, countries, getDebounce, onSearchInput, searchInputNumberDisplay, finalValue, setDefaultMask, resetPhoneInputValidation, setMaskAndCountry, setMask, customPhoneRules, emitValue, onBlur, onPaste, vOmitInputElement, VCol, VLayout, VRow, AutomationTestsIds }
}

})